import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVaultStore, Vault, VaultItem, VaultMember } from '../../../store/vaultStore';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import {
  ArrowLeft, Plus, Eye, EyeOff, Copy, Pencil, Archive, Trash2, KeyRound, Users, Wand2, X, FolderGit2,
} from 'lucide-react';
import { PeoplePicker } from '../../../components/PeoplePicker';
import { useEmployeeStore } from '../../../store/employeeStore';
import {
  CREDENTIAL_TEMPLATES, templateByKey, templateForItemType, CredentialTemplate,
} from '../credentialTemplates';
import toast from 'react-hot-toast';

const UNGROUPED = 'No project';

export function VaultDetail() {
  const { vaultId } = useParams<{ vaultId: string }>();
  const navigate = useNavigate();
  const store = useVaultStore();

  const [vault, setVault] = useState<Vault | null>(null);
  const [items, setItems] = useState<VaultItem[]>([]);
  const [members, setMembers] = useState<VaultMember[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [tab, setTab] = useState<'items' | 'members'>('items');
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState<string>('all');

  // Credential form
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tmplKey, setTmplKey] = useState<string>('website_login');
  const [meta, setMeta] = useState({ title: '', environment: '', project: '' });
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<{ label: string; value: string }[]>([]);
  const [secretEditable, setSecretEditable] = useState(true);
  const [showValues, setShowValues] = useState(false);
  const [saving, setSaving] = useState(false);

  const [reveal, setReveal] = useState<{ title: string; secret: Record<string, any> } | null>(null);
  const [revealShow, setRevealShow] = useState(false);

  const [memberOpen, setMemberOpen] = useState(false);
  const [memberForm, setMemberForm] = useState<any>({ userId: '', role: 'member', canView: true, canReveal: false, canEdit: false, canShare: false, canDelete: false });

  const fetchAllEmployees = useEmployeeStore((s) => s.fetchAllEmployees);
  const [peopleMap, setPeopleMap] = useState<Record<string, { name: string; email: string }>>({});
  useEffect(() => {
    let cancelled = false;
    fetchAllEmployees().then((rows: any[]) => {
      if (cancelled) return;
      const map: Record<string, { name: string; email: string }> = {};
      for (const r of rows) { const uid = r.user_id || r.userId; if (uid) map[uid] = { name: r.displayName || r.email || uid, email: r.email || '' }; }
      setPeopleMap(map);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [fetchAllEmployees]);
  const personLabel = (uid: string) => peopleMap[uid]?.name || uid;

  const loadAll = async () => {
    if (!vaultId) return;
    setLoading(true);
    try {
      const v = await store.getVault(vaultId);
      setVault(v);
      setItems(await store.listItems(vaultId));
      try { setProjects(await store.listProjects(vaultId)); } catch { /* none */ }
      if (v.vaultType === 'team' && v.permissions?.share) {
        try { setMembers(await store.listMembers(vaultId)); } catch { /* not a manager */ }
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load vault');
      navigate('/vault/vaults');
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [vaultId]);

  const canCreate = vault?.permissions?.create ?? false;
  const isManager = !!vault && (vault.permissions?.share ?? false);
  const template: CredentialTemplate = templateByKey(tmplKey);

  // ---- Form helpers --------------------------------------------------------

  const resetForm = (tpl: string) => {
    const t = templateByKey(tpl);
    const fv: Record<string, string> = {};
    t.fields.forEach((f) => { fv[f.key] = ''; });
    setFieldValues(fv);
    setCustomFields([]);
  };

  const openCreate = () => {
    setEditingId(null);
    setTmplKey('website_login');
    setMeta({ title: '', environment: '', project: projectFilter !== 'all' && projectFilter !== UNGROUPED ? projectFilter : '' });
    resetForm('website_login');
    setSecretEditable(true);
    setShowValues(false);
    setFormOpen(true);
  };

  const openEdit = async (it: VaultItem) => {
    const tplKey = it.template || templateForItemType(it.itemType).key;
    const t = templateByKey(tplKey);
    setEditingId(it.id);
    setTmplKey(tplKey);
    setMeta({ title: it.title, environment: it.environment ?? '', project: it.project ?? '' });
    const fv: Record<string, string> = {};
    t.fields.forEach((f) => { fv[f.key] = f.meta ? (it.username ?? '') : ''; });
    const custom: { label: string; value: string }[] = [];

    const canReveal = it.permissions?.reveal;
    setSecretEditable(!!canReveal);
    if (canReveal) {
      try {
        const res = await store.revealItem(it.id);
        const secret = res.secret || {};
        const keys = new Set(t.fields.map((f) => f.key));
        for (const [k, v] of Object.entries(secret)) {
          if (keys.has(k)) fv[k] = String(v ?? '');
          else custom.push({ label: k, value: String(v ?? '') });
        }
      } catch { /* leave blank; still editable metadata */ }
    }
    setFieldValues(fv);
    setCustomFields(custom);
    setShowValues(false);
    setFormOpen(true);
  };

  const onTemplateChange = (key: string) => { setTmplKey(key); if (!editingId) resetForm(key); };

  const generate = async (key: string) => {
    try {
      const pwd = await store.generatePassword({ length: 24, symbols: true });
      setFieldValues((f) => ({ ...f, [key]: pwd }));
      setShowValues(true);
    } catch { toast.error('Could not generate a value'); }
  };

  const buildSecret = () => {
    const secret: Record<string, string> = {};
    for (const f of template.fields) {
      if (f.meta) continue; // username → metadata, not the secret payload
      const v = (fieldValues[f.key] ?? '').trim();
      if (v) secret[f.key] = v;
    }
    for (const c of customFields) {
      const label = c.label.trim();
      if (label && c.value) secret[label] = c.value;
    }
    return secret;
  };

  const metaUsername = () => {
    const metaField = template.fields.find((f) => f.meta);
    return metaField ? (fieldValues[metaField.key] ?? '').trim() : '';
  };

  const submit = async () => {
    if (!vaultId) return;
    if (!meta.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const payload: any = {
          title: meta.title.trim(), environment: meta.environment || null,
          project: meta.project || null, template: tmplKey, username: metaUsername() || null,
        };
        if (secretEditable) payload.secret = buildSecret(); // full payload so nothing is lost
        await store.updateItem(editingId, payload);
        toast.success('Credential updated');
      } else {
        const secret = buildSecret();
        if (Object.keys(secret).length === 0) { toast.error('Enter at least one value'); setSaving(false); return; }
        await store.createItem(vaultId, {
          title: meta.title.trim(), itemType: template.itemType, template: tmplKey,
          project: meta.project || null, environment: meta.environment || null,
          username: metaUsername() || null, secret,
        });
        toast.success('Credential created');
      }
      setFormOpen(false);
      setItems(await store.listItems(vaultId));
      try { setProjects(await store.listProjects(vaultId)); } catch { /* */ }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save credential');
    } finally { setSaving(false); }
  };

  const doReveal = async (it: VaultItem) => {
    try { const res = await store.revealItem(it.id); setRevealShow(false); setReveal({ title: res.title, secret: res.secret }); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'You are not permitted to reveal this credential'); }
  };
  const closeReveal = () => { setReveal(null); setRevealShow(false); };

  const archive = async (it: VaultItem) => {
    try { await store.archiveItem(it.id); toast.success('Archived'); setItems(await store.listItems(vaultId!)); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to archive'); }
  };
  const remove = async (it: VaultItem) => {
    if (!confirm(`Delete "${it.title}"? This cannot be undone.`)) return;
    try { await store.deleteItem(it.id); toast.success('Deleted'); setItems(await store.listItems(vaultId!)); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to delete'); }
  };

  const addMember = async () => {
    if (!vaultId) return;
    if (!memberForm.userId.trim()) { toast.error('Select a person to add'); return; }
    try {
      await store.addMember(vaultId, memberForm);
      toast.success('Member added');
      setMemberOpen(false);
      setMemberForm({ userId: '', role: 'member', canView: true, canReveal: false, canEdit: false, canShare: false, canDelete: false });
      setMembers(await store.listMembers(vaultId));
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to add member'); }
  };
  const revokeMember = async (m: VaultMember) => {
    if (!confirm('Revoke this member\'s access?')) return;
    try { await store.removeMember(vaultId!, m.id); toast.success('Member revoked'); setMembers(await store.listMembers(vaultId!)); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to revoke'); }
  };

  // ---- Grouped item list ---------------------------------------------------

  const grouped = useMemo(() => {
    const filtered = projectFilter === 'all'
      ? items
      : items.filter((it) => (it.project || UNGROUPED) === projectFilter);
    const map = new Map<string, VaultItem[]>();
    for (const it of filtered) {
      const key = it.project || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(it);
    }
    // Projects first (alpha), then the ungrouped bucket last.
    return [...map.entries()].sort((a, b) =>
      a[0] === UNGROUPED ? 1 : b[0] === UNGROUPED ? -1 : a[0].localeCompare(b[0]));
  }, [items, projectFilter]);

  if (loading) return <div className="p-8 text-muted-foreground">Loading vault…</div>;
  if (!vault) return null;

  const secretEntries = reveal ? Object.entries(reveal.secret) : [];

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/vault/vaults')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> All vaults
        </button>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              {vault.vaultType === 'team' ? <Users className="w-5 h-5" /> : <KeyRound className="w-5 h-5" />}
            </div>
            <div>
              <h1 className="tracking-tight text-foreground font-display text-page-title">{vault.name}</h1>
              <p className="text-muted-foreground text-sm capitalize">{vault.vaultType} vault · {vault.status}</p>
            </div>
          </div>
          {tab === 'items' && canCreate && (
            <Button variant="primary" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> New credential</Button>
          )}
          {tab === 'members' && isManager && vault.vaultType === 'team' && (
            <Button variant="primary" onClick={() => setMemberOpen(true)}><Plus className="w-4 h-4 mr-2" /> Add member</Button>
          )}
        </div>
      </div>

      {vault.vaultType === 'team' && (
        <div className="flex gap-1 border-b border-border">
          {(['items', 'members'] as const).map((t) => (
            (t === 'members' && !isManager) ? null : (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}>
                {t === 'items' ? 'Credentials' : 'Members'}
              </button>
            )
          ))}
        </div>
      )}

      {tab === 'items' ? (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <h3 className="text-foreground font-display text-section-title">Credentials</h3>
              {projects.length > 0 && (
                <div className="w-52">
                  <Select value={projectFilter} onValueChange={setProjectFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All projects</SelectItem>
                      {projects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      <SelectItem value={UNGROUPED}>{UNGROUPED}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">No credentials in this vault yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {grouped.map(([group, rows]) => (
                  <div key={group}>
                    <div className="flex items-center gap-2 bg-secondary/40 px-6 py-2 text-xs font-semibold text-muted-foreground">
                      <FolderGit2 className="w-3.5 h-3.5" /> {group}
                      <span className="text-muted-foreground/60">· {rows.length}</span>
                    </div>
                    <table className="w-full text-sm text-left">
                      <tbody className="divide-y divide-border">
                        {rows.map((it) => (
                          <tr key={it.id} className="hover:bg-secondary/20 transition-colors">
                            <td className="px-6 py-3 w-[40%]">
                              <div className="font-medium text-foreground">
                                {it.title}
                                {it.status === 'archived' && <Badge variant="secondary" className="ml-2">Archived</Badge>}
                              </div>
                              {it.username && <div className="text-xs text-muted-foreground">{it.username}</div>}
                            </td>
                            <td className="px-6 py-3">
                              <Badge variant="secondary">{templateByKey(it.template || templateForItemType(it.itemType).key).label}</Badge>
                            </td>
                            <td className="px-6 py-3 text-muted-foreground">{it.environment || '—'}</td>
                            <td className="px-6 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {it.permissions?.reveal && <Button variant="ghost" size="icon-sm" title="Reveal" onClick={() => doReveal(it)}><Eye className="w-4 h-4" /></Button>}
                                {it.permissions?.edit && <Button variant="ghost" size="icon-sm" title="Edit" onClick={() => openEdit(it)}><Pencil className="w-4 h-4" /></Button>}
                                {it.permissions?.edit && it.status === 'active' && <Button variant="ghost" size="icon-sm" title="Archive" onClick={() => archive(it)}><Archive className="w-4 h-4" /></Button>}
                                {it.permissions?.delete && <Button variant="ghost" size="icon-sm" title="Delete" className="text-muted-foreground hover:text-destructive" onClick={() => remove(it)}><Trash2 className="w-4 h-4" /></Button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><h3 className="text-foreground font-display text-section-title">Members</h3></CardHeader>
          <CardContent className="p-0">
            {members.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">No members yet.</div>
            ) : (
              <table className="w-full text-sm text-left">
                <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                  <tr>
                    <th className="px-6 py-3">User</th><th className="px-6 py-3">Role</th>
                    <th className="px-6 py-3">Rights</th><th className="px-6 py-3">Expires</th><th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members.map((m) => (
                    <tr key={m.id} className="hover:bg-secondary/20">
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">{personLabel(m.userId)}</div>
                        {peopleMap[m.userId]?.email && <div className="text-xs text-muted-foreground">{peopleMap[m.userId].email}</div>}
                      </td>
                      <td className="px-6 py-4 capitalize">{m.role}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {m.canReveal && <Badge variant="success">Reveal</Badge>}
                          {m.canEdit && <Badge variant="secondary">Edit</Badge>}
                          {m.canShare && <Badge variant="secondary">Share</Badge>}
                          {m.canDelete && <Badge variant="secondary">Delete</Badge>}
                          {!m.canReveal && !m.canEdit && <Badge variant="muted">View</Badge>}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">{m.expiresAt ? new Date(m.expiresAt).toLocaleDateString() : '—'}</td>
                      <td className="px-6 py-4 text-right">
                        {m.role !== 'owner' && <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive" onClick={() => revokeMember(m)}><Trash2 className="w-4 h-4" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create / edit credential */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit credential' : 'New credential'}</DialogTitle>
            <DialogDescription>Pick a type to get the right fields. Secret values are encrypted before they leave the server.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Type</label>
                <Select value={tmplKey} onValueChange={onTemplateChange} disabled={!!editingId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CREDENTIAL_TEMPLATES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Project <span className="text-muted-foreground">(optional)</span></label>
                <Input list="vault-projects" value={meta.project} onChange={(e) => setMeta((m) => ({ ...m, project: e.target.value }))} placeholder="e.g. Acme Web" />
                <datalist id="vault-projects">{projects.map((p) => <option key={p} value={p} />)}</datalist>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Title</label>
                <Input value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} placeholder="e.g. Production S3" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Environment <span className="text-muted-foreground">(optional)</span></label>
                <Input value={meta.environment} onChange={(e) => setMeta((m) => ({ ...m, environment: e.target.value }))} placeholder="production / staging" />
              </div>
            </div>

            {editingId && !secretEditable && (
              <p className="text-xs text-warning-strong bg-warning/10 rounded-md px-3 py-2">
                You can edit details but not the secret values (reveal permission required).
              </p>
            )}

            {/* Template fields */}
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fields</span>
              {template.fields.some((f) => f.secret !== false) && (
                <Button variant="ghost" size="xs" onClick={() => setShowValues((s) => !s)} className="gap-1">
                  {showValues ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {showValues ? 'Hide' : 'Show'}
                </Button>
              )}
            </div>

            {template.fields.map((f) => {
              const masked = f.secret !== false && !showValues;
              const disabled = !!(editingId && !secretEditable && f.secret !== false && !f.meta);
              return (
                <div key={f.key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">{f.label}</label>
                    {f.generate && !disabled && (
                      <Button variant="ghost" size="xs" onClick={() => generate(f.key)} className="gap-1"><Wand2 className="w-3.5 h-3.5" /> Generate</Button>
                    )}
                  </div>
                  {f.multiline ? (
                    <Textarea rows={3} value={fieldValues[f.key] ?? ''} disabled={disabled}
                      onChange={(e) => setFieldValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      placeholder={f.placeholder} className="font-mono" />
                  ) : (
                    <Input type={masked ? 'password' : 'text'} value={fieldValues[f.key] ?? ''} disabled={disabled}
                      autoComplete="off"
                      onChange={(e) => setFieldValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      placeholder={f.placeholder} className={f.secret !== false ? 'font-mono' : ''} />
                  )}
                </div>
              );
            })}

            {/* Custom fields */}
            {(secretEditable || !editingId) && (
              <div className="space-y-2 pt-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Custom fields</span>
                {customFields.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="Label" value={c.label} className="w-1/3"
                      onChange={(e) => setCustomFields((arr) => arr.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                    <Input placeholder="Value" value={c.value} type={showValues ? 'text' : 'password'} className="flex-1 font-mono" autoComplete="off"
                      onChange={(e) => setCustomFields((arr) => arr.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                    <Button variant="ghost" size="icon-sm" onClick={() => setCustomFields((arr) => arr.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button variant="secondary" size="xs" onClick={() => setCustomFields((arr) => [...arr, { label: '', value: '' }])} className="gap-1">
                  <Plus className="w-3.5 h-3.5" /> Add field
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={submit} isLoading={saving}>{editingId ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal */}
      <Dialog open={!!reveal} onOpenChange={(o) => !o && closeReveal()}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>{reveal?.title}</DialogTitle>
            <DialogDescription>Revealed secret. This view clears when you close it, and the reveal was audited.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button variant="ghost" size="xs" onClick={() => setRevealShow((s) => !s)} className="gap-1">
                {revealShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {revealShow ? 'Hide' : 'Show'}
              </Button>
            </div>
            {secretEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No secret fields.</p>
            ) : secretEntries.map(([k, v]) => (
              <div key={k} className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">{k}</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm font-mono break-all whitespace-pre-wrap">
                    {revealShow ? String(v) : '•'.repeat(Math.min(String(v).length, 24))}
                  </code>
                  <Button variant="ghost" size="icon-sm" title="Copy" onClick={() => { navigator.clipboard?.writeText(String(v)); toast.success('Copied'); }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="secondary" onClick={closeReveal}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add member */}
      <Dialog open={memberOpen} onOpenChange={setMemberOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add member</DialogTitle>
            <DialogDescription>Grant a teammate access to this vault. Permissions are explicit — reveal is off by default.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Person</label>
              <PeoplePicker value={memberForm.userId} excludeUserIds={members.map((m) => m.userId)}
                onChange={(p) => setMemberForm((f: any) => ({ ...f, userId: p?.userId || '' }))}
                placeholder="Search teammates by name or email…" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Role</label>
              <Select value={memberForm.role} onValueChange={(v) => setMemberForm((f: any) => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[['canView', 'View'], ['canReveal', 'Reveal'], ['canCreate', 'Create'], ['canEdit', 'Edit'], ['canShare', 'Share'], ['canDelete', 'Delete']].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-foreground">
                  <input type="checkbox" className="size-4 accent-primary" checked={!!memberForm[key]} onChange={(e) => setMemberForm((f: any) => ({ ...f, [key]: e.target.checked }))} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMemberOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={addMember}>Add member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
